import store from '@/store'
import routes from '@/router/routes'
import { fetchProfile } from '@/services/profile'

/**
 * Mutation types of the store
 */
const mutationTypes = {
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED'
}

/**
 * Initial state
 */
const state = {
  loading: false,
  info: undefined
}

/**
 * Getters
 */
const getters = {
  hasPremiumSubscription(state) {
    return state.info?.subscription?.level === 'GOLD'
  },

  hasValidSubscription(state) {
    return state.info?.subscription?.status === 'active'
  },

  pagesEnabledAccessForTradingBlocker() {
    return routes[1].children
      .filter((route) =>
        route.meta?.enabledAccessFor?.includes('tradingBlocker')
      )
      .map((route) => route.name)
  },

  pagesEnabledAccessForSubscriptionBlocker() {
    return routes[1].children
      .filter((route) =>
        route.meta?.enabledAccessFor?.includes('subscriptionBlocker')
      )
      .map((route) => route.name)
  },

  pagesEnabledAccessForRoleBlocker() {
    return routes[1].children
      .filter((route) => route.meta?.enabledAccessFor?.includes('roleBlocker'))
      .map((route) => route.name)
  },

  pagesEnabledAccessForSupplierDirectPaymentTypeBlocker() {
    return routes[1].children
      .filter((route) =>
        route.meta?.enabledAccessFor?.includes(
          'supplierDirectPaymentTypeBlocker'
        )
      )
      .map((route) => route.name)
  },

  pagesEnabledAccessForPaymentServicesPaymentTypeBlocker() {
    return routes[1].children
      .filter((route) =>
        route.meta?.enabledAccessFor?.includes(
          'paymentServicesPaymentTypeBlocker'
        )
      )
      .map((route) => route.name)
  },

  pagesEnabledAccessForCreditReturnsBlocker() {
    return routes[1].children
      .filter((route) =>
        route.meta?.enabledAccessFor?.includes('creditReturnsBlocker')
      )
      .map((route) => route.name)
  },

  subscriptionStatus(state, getters) {
    let status
    if (!state.info?.subscription?.level) {
      status = 'NO_SUBSCRIPTION'
    } else {
      status = getters.hasValidSubscription
        ? state.info?.subscription?.level
        : 'REVIEW_SUBSCRIPTION'
    }
    return status
  },

  subscriptionStatusDisplayName(state, getters) {
    let statusDisplayName

    switch (getters.subscriptionStatus) {
      case 'REVIEW_SUBSCRIPTION':
        statusDisplayName = 'Review subscription'
        break
      case 'GOLD':
        statusDisplayName = 'Premium'
        break
      case 'SILVER':
        statusDisplayName = 'Basic'
        break
      default:
        statusDisplayName = 'Setup Subscription'
    }

    return statusDisplayName
  },

  userGuideRead(state) {
    return !!state.info?.userGuideRead
  },

  bankDetailsAvailable(state) {
    return !!state.info?.bankDetailsAvailable
  },

  defaultPaymentDays(state) {
    return state.info?.defaultPaymentDays
  },

  creditReturnEnabled(state) {
    return state.info?.creditReturnEnabled
  },

  readyForOptionalSteps(state, getters) {
    return !!(
      (!store.getters['tenant/userGuideEnabled'] || getters.userGuideRead) &&
      getters.bankDetailsAvailable &&
      (store.getters['tenant/featureSubscriptionEnabled']
        ? getters.hasValidSubscription
        : true) &&
      store.getters['freightInfo/validTradingRules']
    )
  },

  readyForPromotionalSteps(state, getters) {
    return (
      getters.readyForOptionalSteps &&
      !!store.getters['products/liveProducts'].length
    )
  },

  readyToTrade(state, getters) {
    return !!(
      (!store.getters['tenant/userGuideEnabled'] || getters.userGuideRead) &&
      (store.getters['tenant/featureBankAccountsEnabled']
        ? getters.bankDetailsAvailable
        : true) &&
      (store.getters['tenant/featureSubscriptionEnabled']
        ? getters.hasValidSubscription
        : true) &&
      store.getters['freightInfo/validTradingRules'] &&
      store.getters['products/hasAnyProducts']
    )
  },

  readyToTradeLoaded(state, getters) {
    return !!(
      state.loading === false &&
      store.state.freightInfo.loading === false &&
      store.state.products.loading === false &&
      store.state.products.presenceLoading === false &&
      store.state.products.presenceLoaded === true
    )
  },

  dataApprovalRequired(state) {
    return !!state.info?.dataApprovalRequired
  },

  processesOrdersOffline(state) {
    return !!state.info?.processesOrdersOffline
  }
}

/**
 * Actions
 */
const actions = {
  fetchSupplierProfile({ commit }, supplierId) {
    commit(mutationTypes.FETCHING)

    return fetchProfile(supplierId)
      .then((supplierProfile) => {
        commit(mutationTypes.FETCHED, supplierProfile)
      })
      .catch((err) => {
        console.log(err)
        commit(mutationTypes.FETCHED, [])
      })
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.FETCHING](state) {
    state.loading = true
  },

  [mutationTypes.FETCHED](state, supplierProfile) {
    state.info = supplierProfile
    state.loading = false
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
